/**
 * O HISTÓRICO DESTE NAVEGADOR — cada questão respondida no percurso.
 *
 * Alimenta a TRILHA do mapa (o que o aluno já domina e o que vem a seguir) e o
 * relatório. Fica no `localStorage`, com um teto de registros; como a configuração do
 * percurso, o formato é versionado (`v: 1`) para migrar a uma plataforma depois.
 *
 * O laboratório não entra aqui: lá se experimenta item, não se estuda.
 */

import type { ItemInstance } from '../schema/item.ts';
import type { GradingResult } from '../schema/response.ts';
import { FICHAS } from './habilidades.ts';

export interface Tentativa {
  /** Relógio de parede, em milissegundos. */
  readonly t: number;
  readonly templateId: string;
  readonly seed: string;
  readonly correto: boolean;
  readonly score: number;
  /** O erro nomeado, se houve. */
  readonly erro: string | null;
  readonly dicas: number;
}

const CHAVE = 'itens-iterativos:historico';
const TETO = 3000;

/** Disparado em `window` sempre que o histórico muda. */
export const EVENTO_HISTORICO = 'itens-iterativos:historico-mudou';

const valida = (x: unknown): x is Tentativa => {
  const t = x as Partial<Tentativa> | null;
  return (
    !!t &&
    typeof t.templateId === 'string' &&
    typeof t.correto === 'boolean' &&
    typeof t.t === 'number'
  );
};

export function carregarHistorico(): Tentativa[] {
  try {
    const bruto = localStorage.getItem(CHAVE);
    if (bruto) {
      const o = JSON.parse(bruto) as { v?: unknown; tentativas?: unknown } | null;
      if (o && o.v === 1 && Array.isArray(o.tentativas)) return o.tentativas.filter(valida);
    }
  } catch {
    // armazenamento bloqueado ou corrompido: começa vazio
  }
  return [];
}

function salvar(ts: readonly Tentativa[]): void {
  try {
    localStorage.setItem(CHAVE, JSON.stringify({ v: 1, tentativas: ts.slice(-TETO) }));
  } catch {
    // sem persistência: o histórico vale só para esta sessão
  }
  window.dispatchEvent(new CustomEvent(EVENTO_HISTORICO));
}

/**
 * Uma questão corrigida, no formato que o histórico guarda e que o código R1 transmite.
 *
 * Num lugar só porque eram dois: o histórico montava a tentativa com a hora da
 * correção, e o fecho do percurso montava de novo, com a hora do FIM — e todas as
 * respostas de um aluno chegavam ao professor com o mesmo horário.
 */
export function tentativaDe(inst: ItemInstance, r: GradingResult, t: number): Tentativa {
  return {
    t,
    templateId: inst.templateId as string,
    seed: inst.seed as string,
    correto: r.correct,
    score: r.score,
    erro: (r.evidence.matchedError as string | null) ?? null,
    dicas: r.evidence.dicasUsadas ?? 0,
  };
}

export function registrarTentativa(
  inst: ItemInstance,
  r: GradingResult,
  agora: number = Date.now(),
): void {
  const ts = carregarHistorico();
  ts.push(tentativaDe(inst, r, agora));
  salvar(ts);
}

export function limparHistorico(): void {
  salvar([]);
}

/**
 * - NOVA: nunca respondida.
 * - DOMINADA: as duas respostas mais recentes certas, sem dica. Não basta acertar uma
 *   vez — uma variante pode ter saído fácil —, e acertar com dica é aprender, não
 *   dominar.
 * - PRATICANDO: o resto.
 */
export type Situacao = 'nova' | 'praticando' | 'dominada';

export function situacaoDe(templateId: string, hist: readonly Tentativa[]): Situacao {
  const ts = hist.filter((x) => x.templateId === templateId);
  if (ts.length === 0) return 'nova';
  const ultimas = ts.slice(-2);
  return ultimas.length === 2 && ultimas.every((x) => x.correto && x.dicas === 0)
    ? 'dominada'
    : 'praticando';
}

/**
 * PRÓXIMAS ETAPAS: as habilidades ainda não dominadas cujos pré-requisitos já estão todos
 * dominados. É a fronteira da trilha — o que faz sentido estudar agora.
 */
export function proximasEtapas(hist: readonly Tentativa[]): string[] {
  return FICHAS.filter(
    (f) =>
      situacaoDe(f.templateId, hist) !== 'dominada' &&
      f.prerequisitos.every((p) => situacaoDe(p, hist) === 'dominada'),
  ).map((f) => f.templateId);
}

export function contagemDe(
  templateId: string,
  hist: readonly Tentativa[],
): { readonly tentativas: number; readonly acertos: number } {
  const ts = hist.filter((x) => x.templateId === templateId);
  return { tentativas: ts.length, acertos: ts.filter((x) => x.correto).length };
}
